"use client"

import { Link } from 'react-router-dom'
import { ArrowLeft, AlertTriangle, Mic } from 'lucide-react'
import * as stylex from '@stylexjs/stylex'
import { AudioVideoToVideoGallery } from '@/components/gallery/AudioVideoToVideoGallery'
import { HeroVideoPlayer } from '@/components/HeroVideoPlayer'
import { VideoGalleryItem } from '@/models/gallery'
// Importing the StyleX styles
import { videoPageStyles } from '@/styles/VideoPage.stylex'

// Model configuration - single source of truth
const MODEL_CONFIG = {
    name: 'Pixverse Lipsync',
    shortName: 'Pixverse',
    modelKey: 'pixverse-lipsync',
    creditsPerVideo: 25,
    heroBgVideo: 'https://cdn.aibrush.co/docs/videos/models/pixverse-lipsync/hero-bg.mp4',
    heroBgPoster: 'https://cdn.aibrush.co/docs/videos/models/pixverse-lipsync/hero-bg.png',
    providerUrl: 'https://app.pixverse.ai/',
    providerName: 'AI Pixverse',
    pageUrl: 'https://docs.aibrush.co/videos-models/pixverse-lipsync',
} as const;

// Static gallery data
const audioVideoToVideoGalleryItems: VideoGalleryItem[] = [
    {
        id: "pixverse-lipsync-1",
        aspect_ratio: "16:9",
        videoSrc: "https://cdn.aibrush.co/docs/videos/models/pixverse-lipsync/sample-1.mp4",
        thumbnailSrc: "https://cdn.aibrush.co/docs/videos/models/pixverse-lipsync/thumbnail-1.jpg",
        className: "",
        prompt: "NaN", // Text prompt is ignored for lipsync
        referenceVideos: ["https://cdn.aibrush.co/docs/videos/models/pixverse-lipsync/reference-1.mp4"],
        referenceAudios: ["https://cdn.aibrush.co/docs/videos/models/pixverse-lipsync/audio-1.wav"]
    },
    {
        id: "pixverse-lipsync-2",
        aspect_ratio: "16:9",
        videoSrc: "https://cdn.aibrush.co/docs/videos/models/pixverse-lipsync/sample-2.mp4",
        thumbnailSrc: "https://cdn.aibrush.co/docs/videos/models/pixverse-lipsync/thumbnail-2.png",
        className: "",
        prompt: "NaN", // Text prompt is ignored for lipsync
        referenceVideos: ["https://cdn.aibrush.co/docs/videos/models/pixverse-lipsync/reference-2.mp4"],
        referenceAudios: ["https://cdn.aibrush.co/docs/videos/models/pixverse-lipsync/audio-2.wav"]
    }
];

const allReferenceVideos: string[] = [
    "https://cdn.aibrush.co/docs/videos/models/pixverse-lipsync/reference-1.mp4",
    "https://cdn.aibrush.co/docs/videos/models/pixverse-lipsync/reference-2.mp4"
];

const allReferenceAudios: string[] = [
    "https://cdn.aibrush.co/docs/videos/models/pixverse-lipsync/audio-1.wav",
    "https://cdn.aibrush.co/docs/videos/models/pixverse-lipsync/audio-2.wav"
];

export default function PixverseLipsyncPage() {
    return (
        <div {...stylex.props(videoPageStyles.modelPage)}>
            {/* Header Navigation */}
            <Link to="/" {...stylex.props(videoPageStyles.backLink)}>
                <ArrowLeft size={20} />
                Back to Home
            </Link>

            {/* Hero Section */}
            <section {...stylex.props(videoPageStyles.heroSection)}>
                <HeroVideoPlayer
                    videoSrc={MODEL_CONFIG.heroBgVideo}
                    posterSrc={MODEL_CONFIG.heroBgPoster}
                />
                <div {...stylex.props(videoPageStyles.heroContent)}>
                    <h2 {...stylex.props(videoPageStyles.heroTitle)}>{MODEL_CONFIG.name.toUpperCase()}</h2>
                    <p {...stylex.props(videoPageStyles.heroDescription)}>
                        Experience professional-grade AI lip synchronization with <strong>Audio & Video to Video</strong> capabilities.
                        Create perfectly synchronized videos where character mouth movements match speech audio with remarkable precision.
                        <br />
                        Powered by <a href={MODEL_CONFIG.providerUrl} target="_blank" rel="noopener noreferrer">{MODEL_CONFIG.providerName}</a>
                    </p>
                    <div {...stylex.props(videoPageStyles.heroStats)}>
                        <div {...stylex.props(videoPageStyles.stat)}>
                            <Mic size={20} {...stylex.props(videoPageStyles.statSvg)} />
                            <span>Lip Synchronization</span>
                        </div>
                    </div>
                    <div {...stylex.props(videoPageStyles.creditCostBadge)}>{MODEL_CONFIG.creditsPerVideo} credits per video</div>
                </div>
            </section>

            {/* Important Note */}
            <div {...stylex.props(videoPageStyles.importantNote)}>
                <div {...stylex.props(videoPageStyles.noteIcon)}>
                    <AlertTriangle size={24} />
                </div>
                <div>
                    <h4 {...stylex.props(videoPageStyles.noteContentH4)}>Important: How {MODEL_CONFIG.name} Works</h4>
                    <p {...stylex.props(videoPageStyles.noteContentP)}>
                        <strong>{MODEL_CONFIG.name} is a specialized model</strong> that requires both a video reference and an audio reference to function:
                    </p>
                    <ul {...stylex.props(videoPageStyles.noteContentUl)}>
                        <li {...stylex.props(videoPageStyles.noteContentLi)}>
                            <strong>Video Reference:</strong> A video of a person/character speaking (can be silent or have different audio)
                        </li>
                        <li {...stylex.props(videoPageStyles.noteContentLi)}>
                            <strong>Audio Reference:</strong> The speech audio you want to synchronize with the video
                        </li>
                        <li {...stylex.props(videoPageStyles.noteContentLi)}>
                            <strong>Text Prompt:</strong> <em>Not used</em> - the model automatically handles lip synchronization
                        </li>
                    </ul>
                    <p {...stylex.props(videoPageStyles.noteContentP)}>
                        The model analyzes both inputs and generates a new video where the character's mouth movements,
                        facial expressions, and head movements are perfectly synchronized with the provided audio.
                        This is ideal for dubbing, language localization, or creating content in different languages.
                    </p>
                </div>
            </div>


            {/* Audio & Video to Video Gallery */}
            <AudioVideoToVideoGallery
                modelKey={MODEL_CONFIG.modelKey}
                modelLabel={MODEL_CONFIG.name}
                modelNote={`This model costs <strong>${MODEL_CONFIG.creditsPerVideo} credits</strong> per video and requires both video and audio references.`}
                title="Audio & Video to Video Gallery"
                description={`Experience advanced lip synchronization using <a href='${MODEL_CONFIG.providerUrl}' target='_blank' rel='noopener noreferrer'>${MODEL_CONFIG.providerName}</a>. Transform silent videos into perfectly synchronized content. <strong>Click on any video</strong> to see the original video, audio, and the synchronized result.`}
                items={audioVideoToVideoGalleryItems}
                allVideos={allReferenceVideos}
                allAudios={allReferenceAudios}
            />
        </div>
    )
}